import { Component, EventEmitter, Input, OnChanges, SimpleChanges, OnInit, Output, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { FormService } from './form.service';
import { isEmpty, forEach, isBoolean } from 'lodash';
import { IFormOptions, IButtonObj } from './form.interface';
import { ToastrService } from 'ngx-toastr';
import { NgbDateAdapter, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { CustomAdapter, CustomDateParserFormatter } from '../ngb-adaptor'
import { DateFormat } from '../shared.const';

@Component({
    selector: 'app-form',
    templateUrl: './form.component.html',
    styleUrls: ['./form.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        { provide: NgbDateAdapter, useClass: CustomAdapter },
        { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }
    ]
})
export class FormOptionComponent implements OnInit, OnChanges {
    @Input() formOptions: IFormOptions[] = [];
    @Input() formData: any = {};
    @Input() submitButton: string = "Senden";
    @Input() cancelButton: string = "Stornieren";
    @Input() footerButtons: IButtonObj[] = [];

    @Output() formEmitter: EventEmitter<any> = new EventEmitter();

    form: FormGroup;

    allFieldArray: any[] = [];
    fieldArray: any[] = [];

    DateFormat = DateFormat;

    constructor(private formService: FormService,
        private toastrService: ToastrService,
        private crdf: ChangeDetectorRef) { }

    ngOnInit(): void {
        this.setForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        for (const propName in changes) {
            if (changes.hasOwnProperty(propName)) {
                let change = changes[propName];
                this.setForm();
            }
        }
    }

    updateOptions(formOptions: IFormOptions[], formData: any, submitButton: string, cancelButton: string, footerButtons: IButtonObj[]) {
        this.formOptions = formOptions;
        this.formData = formData;
        this.submitButton = submitButton;
        this.cancelButton = cancelButton;
        this.footerButtons = footerButtons;
        this.setForm();
    }

    setForm(): void {
        this.allFieldArray = [...this.formOptions];
        this.fieldArray = [...this.formOptions];

        this.formOptions.forEach(option => {
            if (option.readonly && option.validators?.required) {
                option.validators.required = false;
            }
        })

        this.form = this.formService.createForm(this.formOptions);
        if (!isEmpty(this.formData)) {
            for (const propName in this.formData) {
                if (this.formData.hasOwnProperty(propName)) {
                    var formOption = this.formOptions.find(a => a.controlName === propName);
                    if (formOption) {
                        let multiOption = formOption.ddlOptions?.multiOption;
                        let defaultValue = (formOption.defaultValue === null || formOption.defaultValue === undefined) ? (multiOption ? [] : null) : formOption.defaultValue;
                        if (formOption.type === 'dropdown') {
                            if (isEmpty(this.formData[propName])) {
                                this.formData[propName] = defaultValue;
                            }
                        }
                        if (formOption.type === 'checkbox') {
                            if (!isBoolean(this.formData[propName])) {
                                this.formData[propName] = false;
                            }
                        }
                    }
                }
            }
            this.form.patchValue(this.formData, { emitEvent: true });
        }

        this.allFieldArray.forEach((key: IFormOptions) => {
            if (key.dependentControl) {
                const value = this.form.controls[key.controlName].value;
                this.fieldArray = [...this.formService.dependentEvent(this.form, key.dependentControls, value, this.allFieldArray)];
                this.form.controls[key.controlName].valueChanges.subscribe(value => {
                    if (key.dependentControl) {
                        this.fieldArray = [...this.formService.dependentEvent(this.form, key.dependentControls, value, this.allFieldArray)];
                    }
                })
            }
        });
        this.crdf.detectChanges();
    }

    hiddenControl(field: IFormOptions) {
        if (field.dependentControl) {
            let hidden = false;
            forEach(this.fieldArray, (obj: IFormOptions) => {
                if (obj.dependentControls) {
                    const value = this.form.get(obj.controlName).value;
                    forEach(obj.dependentControls, key => {
                        if (key.conrtols.indexOf(field.controlName) >= 0) {
                            if (value === key.value) {
                                hidden = false;
                                this.form.get(field.controlName).enable();
                            } else {
                                hidden = true;
                                this.form.get(field.controlName).disable();
                            }
                        }
                    })
                }
            })

            return hidden;
        }
        return field.hidden;
    }

    getErrorMsg(option: IFormOptions): string {
        if (!this.form.get(option.controlName).touched) {
            return null;
        }
        return this.formService.errorMessage(this.form, option);
    }

    saveFormData(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            this.toastrService.error("Bitte validieren Sie alle Felder.");
        } else {
            var formRawData = this.form.getRawValue();
            for (const propName in formRawData) {
                if (formRawData.hasOwnProperty(propName)) {
                    if (propName === "undefined" || propName === "") {
                        delete formRawData[propName];
                    }
                }
            }
            this.formEmitter.emit({ type: 'save', value: formRawData });
        }
    }

    setMinMaxDate(field: IFormOptions) {
        if (field?.minDate?.dependentBy && field?.minDate?.dependentTo) {
            let value = this.form.controls[field.minDate.dependentBy].value;
            if (value && value._isValid) {
                value = new Date(value);
            } else {
                value = new Date();
            }
            this.fieldArray.find(a => a.controlName === field.minDate.dependentTo).minDate.date = value;
        }
        if (field?.maxDate?.dependentBy && field?.maxDate?.dependentTo) {
            let value = this.form.controls[field.maxDate.dependentBy].value;
            if (value && value._isValid) {
                value = new Date(value);
            } else {
                value = new Date();
            }
            this.fieldArray.find(a => a.controlName === field.maxDate.dependentTo).maxDate.date = value;
        }
    }

    checkPasswords: ValidatorFn = (group: AbstractControl): ValidationErrors => {
        return { passwordMissmatch: true }
    }

    setPasswordValidator(field: IFormOptions) {
        if (field?.passwordValidation?.dependentBy && field?.passwordValidation?.dependentTo) {
            let dependentBy = this.form.controls[field.passwordValidation.dependentBy].value;
            let dependentTo = this.form.controls[field.passwordValidation.dependentTo].value;

            if (dependentBy === dependentTo) {
                this.form.controls[field.passwordValidation.dependentTo].removeValidators(this.checkPasswords);
            } else {
                if (!this.form.controls[field.passwordValidation.dependentTo].hasValidator(this.checkPasswords)) {
                    this.form.controls[field.passwordValidation.dependentTo].addValidators(this.checkPasswords);
                }
            }
            this.form.controls[field.passwordValidation.dependentTo].updateValueAndValidity();
        }
    }

    triggerDatePikcer(field: IFormOptions, datepicker) {
        this.setMinMaxDate(field);
        datepicker.open();
    }

    changeFunction(field: IFormOptions) {
        if (field.type === 'date') {
            this.setMinMaxDate(field);
        }

        if (field.changeFunction) {
            let value = this.form.controls[field.controlName].value;
            let data = this.form.getRawValue()
            field.changeFunction({ value, data });
        }
    }

    buttonClicked(button: IButtonObj): void {
        if (button.validateForm) {
            this.form.markAllAsTouched();
        }
        if (button.validateForm && this.form.invalid) {
            this.toastrService.error("Bitte validieren Sie alle Felder.");
        } else {
            var formRawData = this.form.getRawValue();
            for (const propName in formRawData) {
                if (formRawData.hasOwnProperty(propName)) {
                    if (propName === "undefined" || propName === "") {
                        delete formRawData[propName];
                    }
                }
            }
            button.onButtonClick({ data: formRawData });
        }
    }

    fnCancelBtn() {
        this.formEmitter.emit({ type: 'cancel' });
    }
}
